import type { IDietPlan, IUserProfile } from '@/hooks/useAppState';
import userStorage from './user-storage';
import { readMemberPlan, effectiveMemberDay } from './memberTrainingDay';
import { getCarbDayFromTrainingDay } from '@/data/training-plan-generator';
import { generatePlanMealFoods, applyAvoidReplacementsToPlan } from '@/data/diet-plan-templates';
import { resolveDietDate } from '@/lib/dietDate';
import type { DietDaySnapshot } from './dietService';

// One-time bridge for a day already recorded before daily targets were saved.
// Historical dates with unknown targets are left unknown rather than reconstructed.
export function legacyDietDay(plan: IDietPlan, profile: IUserProfile): DietDaySnapshot | null {
  const date = new Date(), key = date.toDateString();
  if (plan.dailySnapshots?.[key]) return null;
  let statuses: Record<string, string> = {};
  try { statuses = JSON.parse(userStorage.getItem(`diet_checkin_${key}`) || '{}'); } catch { /* unknown */ }
  if (!Object.values(statuses).some(status => ['completed', 'adjusted', 'actual', 'skipped'].includes(status))
    && !Object.keys(plan.dailyMealFoods?.[key] || {}).length) return null;
  const training = readMemberPlan();
  const memberDay = training ? effectiveMemberDay(training, date) : null;
  const state = resolveDietDate(date, { plan, profile, memberDay,
    memberCarbDay: memberDay ? getCarbDayFromTrainingDay(memberDay.type) : undefined });
  if (!state.targets) return null;
  const generated = generatePlanMealFoods(plan.planTemplate, state.targets, plan.gender, state.dayIndex,
    { isTrainingDay: state.isTrainingDay, trainingTimeSlot: plan.trainingTimeSlot });
  const foods = applyAvoidReplacementsToPlan(generated, [...(plan.avoidFoods || []), ...(plan.allergens || [])]);
  if (plan.mealsPerDay === 3) {
    for (const food of foods.snack) {
      foods.lunch.push({ ...food, grams: food.grams / 2 });
      foods.dinner.push({ ...food, grams: food.grams / 2 });
    }
    foods.snack = [];
  }
  return { ...state, targets: state.targets, template: plan.planTemplate,
    mealFoods: { ...foods, ...plan.dailyMealFoods?.[key] }, capturedAt: Date.now() };
}
